import { SCHEMES } from '@/components/EchartCustomized'
import dayjs from 'dayjs'
import html2canvas from 'html2canvas'
import {
    ImageDown,
    Lightbulb,
    Minus,
    TrendingDown,
    TrendingUp,
} from 'lucide-react'
import { useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import {
    Area,
    AreaChart,
    CartesianGrid,
    Legend,
    Line,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts'
import { Button, Typogaphy } from 'vnetwork-ui'
import { ServiceMonthlyReportItem } from '../types'

interface CancellationRateChartProps {
    data: ServiceMonthlyReportItem[]
    previousYearData?: ServiceMonthlyReportItem[]
    showComparison: boolean
    year: number
}

const CancellationRateChart: React.FC<CancellationRateChartProps> = ({
    data,
    previousYearData,
    showComparison,
    year,
}) => {
    const { t } = useTranslation()
    const chartRef = useRef<HTMLDivElement>(null)

    const chartData = useMemo(() => {
        return data.map((item) => {
            const prevItem = previousYearData?.find(
                (p) => p.month.slice(5) === item.month.slice(5)
            )
            return {
                month: item.month,
                monthLabel: dayjs(item.month + '-01').format('MMM'),
                cancellation_rate: item.cancellation_rate,
                retention_rate: item.retention_rate,
                prev_cancellation_rate: prevItem?.cancellation_rate,
                prev_retention_rate: prevItem?.retention_rate,
            }
        })
    }, [data, previousYearData])

    // Calculate averages
    const avgCancellationRate = useMemo(() => {
        if (data.length === 0) return 0
        return (
            data.reduce((sum, d) => sum + d.cancellation_rate, 0) / data.length
        )
    }, [data])

    const prevAvgCancellationRate = useMemo(() => {
        if (!previousYearData || previousYearData.length === 0) return null
        return (
            previousYearData.reduce((sum, d) => sum + d.cancellation_rate, 0) /
            previousYearData.length
        )
    }, [previousYearData])

    const rateChange = useMemo(() => {
        if (prevAvgCancellationRate === null) return null
        return avgCancellationRate - prevAvgCancellationRate
    }, [avgCancellationRate, prevAvgCancellationRate])

    // Analysis data
    const analysis = useMemo(() => {
        if (data.length < 2) return null

        const avgRetention =
            data.reduce((s, d) => s + d.retention_rate, 0) / data.length

        // Find best/worst months
        const sortedByRate = [...data].sort(
            (a, b) => b.cancellation_rate - a.cancellation_rate
        )
        const highestRateMonth = sortedByRate[0]
        const lowestRateMonth = sortedByRate[sortedByRate.length - 1]

        // Calculate volatility (standard deviation)
        const variance =
            data.reduce(
                (s, d) =>
                    s + Math.pow(d.cancellation_rate - avgCancellationRate, 2),
                0
            ) / data.length
        const volatility = Math.sqrt(variance)

        // Trend (compare first half vs second half)
        const firstHalf = data.slice(0, Math.ceil(data.length / 2))
        const secondHalf = data.slice(Math.ceil(data.length / 2))
        const firstHalfAvg =
            firstHalf.reduce((s, d) => s + d.cancellation_rate, 0) /
            firstHalf.length
        const secondHalfAvg =
            secondHalf.reduce((s, d) => s + d.cancellation_rate, 0) /
            secondHalf.length
        const trendDirection =
            secondHalfAvg < firstHalfAvg
                ? 'improving'
                : secondHalfAvg > firstHalfAvg
                  ? 'worsening'
                  : 'stable'

        return {
            avgRetention,
            highestRateMonth,
            lowestRateMonth,
            volatility,
            trendDirection,
        }
    }, [data, avgCancellationRate])

    const exportToImage = async () => {
        if (!chartRef.current) return
        const canvas = await html2canvas(chartRef.current, {
            backgroundColor: '#ffffff',
            scale: 2,
        })
        const link = document.createElement('a')
        link.download = `cancellation_rate_${year}.png`
        link.href = canvas.toDataURL('image/png')
        link.click()
    }

    const renderRateChangeIndicator = () => {
        if (rateChange === null) return null

        // For cancellation rate, decrease is good (green), increase is bad (red)
        if (rateChange < 0) {
            return (
                <div className="flex items-center gap-1 text-green-600">
                    <TrendingDown size={16} />
                    <span className="text-sm font-medium">
                        {rateChange.toFixed(2)}%
                    </span>
                </div>
            )
        } else if (rateChange > 0) {
            return (
                <div className="flex items-center gap-1 text-red-600">
                    <TrendingUp size={16} />
                    <span className="text-sm font-medium">
                        +{rateChange.toFixed(2)}%
                    </span>
                </div>
            )
        }
        return (
            <div className="flex items-center gap-1 text-gray-500">
                <Minus size={16} />
                <span className="text-sm font-medium">0%</span>
            </div>
        )
    }

    const CustomTooltip = ({ active, payload }: any) => {
        if (!active || !payload || !payload.length) return null

        // Get the original month from chartData payload
        const chartItem = payload[0]?.payload
        const originalMonth = chartItem?.month

        const monthData = data.find((d) => d.month === originalMonth)
        const prevMonthData = previousYearData?.find(
            (p) => p.month.slice(5) === originalMonth?.slice(5)
        )

        return (
            <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg">
                <p className="font-semibold text-gray-800 mb-2">
                    {dayjs(originalMonth + '-01').format('MMMM YYYY')}
                </p>
                <div className="space-y-1 text-sm">
                    <p className="text-red-600">
                        {t('cancellation_rate')}:{' '}
                        <span className="font-medium">
                            {monthData?.cancellation_rate.toFixed(1)}%
                        </span>
                    </p>
                    <p className="text-green-600">
                        {t('retention_rate')}:{' '}
                        <span className="font-medium">
                            {monthData?.retention_rate.toFixed(1)}%
                        </span>
                    </p>
                    {showComparison && prevMonthData && (
                        <div className="border-t border-gray-200 pt-1 mt-1">
                            <p className="text-red-400">
                                {t('cancellation_rate')} {year - 1}:{' '}
                                <span className="font-medium">
                                    {prevMonthData.cancellation_rate.toFixed(1)}
                                    %
                                </span>
                            </p>
                            <p
                                className={
                                    monthData!.cancellation_rate -
                                        prevMonthData.cancellation_rate <=
                                    0
                                        ? 'text-green-600'
                                        : 'text-red-600'
                                }
                            >
                                {t('change')}:{' '}
                                <span className="font-medium">
                                    {monthData!.cancellation_rate -
                                        prevMonthData.cancellation_rate >=
                                    0
                                        ? '+'
                                        : ''}
                                    {(
                                        monthData!.cancellation_rate -
                                        prevMonthData.cancellation_rate
                                    ).toFixed(2)}
                                    %
                                </span>
                            </p>
                        </div>
                    )}
                </div>
            </div>
        )
    }

    return (
        <div
            ref={chartRef}
            className="bg-white rounded-sm p-4 shadow-sm border border-gray-100"
        >
            <div className="flex items-center justify-between mb-4">
                <div className="flex flex-col gap-1">
                    <Typogaphy className="text-primary font-semibold">
                        {t('cancellation_retention_rate')}
                    </Typogaphy>
                    <Typogaphy className="text-xs text-gray-500">
                        {t('monthly_rate_analysis')}
                    </Typogaphy>
                </div>
                <div className="flex items-center gap-4">
                    <div
                        className="text-right"
                        data-tour="cancellation-rate-avg"
                    >
                        <div className="flex items-center gap-2 justify-end">
                            <Typogaphy className="text-2xl font-bold text-red-600">
                                {avgCancellationRate.toFixed(2)}%
                            </Typogaphy>
                            {renderRateChangeIndicator()}
                        </div>
                        <Typogaphy className="text-xs text-gray-500">
                            {t('avg_cancellation_rate')}
                            {prevAvgCancellationRate !== null && (
                                <span className="ml-1 text-gray-400">
                                    ({t('previous')}:{' '}
                                    {prevAvgCancellationRate.toFixed(2)}%)
                                </span>
                            )}
                        </Typogaphy>
                    </div>
                    <Button
                        variant="outlined"
                        size="small"
                        onClick={exportToImage}
                        className="!min-w-0 !px-2"
                        title={t('download_image')}
                    >
                        <ImageDown size={16} />
                    </Button>
                </div>
            </div>

            {chartData.length === 0 ? (
                <div className="flex justify-center items-center h-[350px] text-gray-500">
                    {t('no_data_available')}
                </div>
            ) : (
                <ResponsiveContainer width="100%" height={350}>
                    <AreaChart data={chartData}>
                        <defs>
                            <linearGradient
                                id="colorRetention"
                                x1="0"
                                y1="0"
                                x2="0"
                                y2="1"
                            >
                                <stop
                                    offset="5%"
                                    stopColor={SCHEMES.success[0]}
                                    stopOpacity={0.3}
                                />
                                <stop
                                    offset="95%"
                                    stopColor={SCHEMES.success[0]}
                                    stopOpacity={0}
                                />
                            </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                        <XAxis
                            dataKey="monthLabel"
                            stroke="#9ca3af"
                            fontSize={12}
                        />
                        <YAxis
                            stroke="#9ca3af"
                            fontSize={12}
                            domain={[0, 100]}
                            tickFormatter={(value) => `${value}%`}
                        />
                        <Tooltip content={<CustomTooltip />} />
                        <Legend
                            content={({ payload }) => (
                                <div className="flex justify-center gap-6 mt-2">
                                    {payload?.map((entry, index) => (
                                        <div
                                            key={`legend-${index}`}
                                            className="flex items-center gap-2"
                                        >
                                            {entry.dataKey ===
                                            'prev_cancellation_rate' ? (
                                                // Dashed line icon for previous year
                                                <svg
                                                    width="20"
                                                    height="12"
                                                    viewBox="0 0 20 12"
                                                >
                                                    <line
                                                        x1="0"
                                                        y1="6"
                                                        x2="20"
                                                        y2="6"
                                                        stroke={entry.color}
                                                        strokeWidth="2"
                                                        strokeDasharray="4 2"
                                                    />
                                                </svg>
                                            ) : entry.type === 'line' ? (
                                                // Solid line icon
                                                <svg
                                                    width="20"
                                                    height="12"
                                                    viewBox="0 0 20 12"
                                                >
                                                    <line
                                                        x1="0"
                                                        y1="6"
                                                        x2="20"
                                                        y2="6"
                                                        stroke={entry.color}
                                                        strokeWidth="2"
                                                    />
                                                </svg>
                                            ) : (
                                                // Area icon (gradient square)
                                                <div
                                                    className="w-3 h-3 rounded-sm opacity-60"
                                                    style={{
                                                        backgroundColor:
                                                            entry.color,
                                                    }}
                                                />
                                            )}
                                            <span className="text-sm text-gray-600">
                                                {entry.value}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            )}
                        />

                        {/* Retention rate area */}
                        <Area
                            type="monotone"
                            dataKey="retention_rate"
                            name={t('retention_rate')}
                            stroke={SCHEMES.success[0]}
                            strokeWidth={2}
                            fill="url(#colorRetention)"
                        />

                        {/* Cancellation rate line */}
                        <Line
                            type="monotone"
                            dataKey="cancellation_rate"
                            name={t('cancellation_rate')}
                            stroke={SCHEMES.error[0]}
                            strokeWidth={2}
                            dot={{
                                fill: SCHEMES.error[0],
                                strokeWidth: 2,
                                r: 4,
                            }}
                        />

                        {/* Previous year comparison */}
                        {showComparison && previousYearData && (
                            <Line
                                type="monotone"
                                dataKey="prev_cancellation_rate"
                                name={`${t('cancellation_rate')} ${year - 1}`}
                                stroke={SCHEMES.error[3]}
                                strokeWidth={1.5}
                                strokeDasharray="5 5"
                                dot={{
                                    fill: SCHEMES.error[3],
                                    strokeWidth: 1,
                                    r: 3,
                                }}
                            />
                        )}
                    </AreaChart>
                </ResponsiveContainer>
            )}

            {/* Analysis Section */}
            {analysis && (
                <div
                    className="mt-4 pt-4 border-t border-gray-200"
                    data-tour="cancellation-analysis"
                >
                    <div className="flex items-center gap-2 mb-3">
                        <Lightbulb size={16} className="text-amber-500" />
                        <span className="text-sm font-medium text-gray-700">
                            {t('reports.analysis.key_insights')}
                        </span>
                    </div>
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                        <div className="bg-green-50 rounded-lg p-3">
                            <p className="text-xs text-gray-500 mb-1">
                                {t('avg_retention_rate')}
                            </p>
                            <p className="text-lg font-semibold text-green-600">
                                {analysis.avgRetention.toFixed(1)}%
                            </p>
                        </div>
                        <div className="bg-red-50 rounded-lg p-3">
                            <p className="text-xs text-gray-500 mb-1">
                                {t('reports.analysis.highest_cancel_month')}
                            </p>
                            <p className="text-lg font-semibold text-red-600">
                                {dayjs(
                                    analysis.highestRateMonth.month + '-01'
                                ).format('MMM')}
                            </p>
                            <p className="text-xs text-gray-500">
                                {analysis.highestRateMonth.cancellation_rate.toFixed(
                                    1
                                )}
                                %
                            </p>
                        </div>
                        <div className="bg-blue-50 rounded-lg p-3">
                            <p className="text-xs text-gray-500 mb-1">
                                {t('reports.analysis.lowest_cancel_month')}
                            </p>
                            <p className="text-lg font-semibold text-blue-600">
                                {dayjs(
                                    analysis.lowestRateMonth.month + '-01'
                                ).format('MMM')}
                            </p>
                            <p className="text-xs text-gray-500">
                                {analysis.lowestRateMonth.cancellation_rate.toFixed(
                                    1
                                )}
                                %
                            </p>
                        </div>
                        <div
                            className={`rounded-lg p-3 ${
                                analysis.trendDirection === 'improving'
                                    ? 'bg-green-50'
                                    : analysis.trendDirection === 'worsening'
                                      ? 'bg-red-50'
                                      : 'bg-gray-50'
                            }`}
                        >
                            <p className="text-xs text-gray-500 mb-1">
                                {t('reports.analysis.trend')}
                            </p>
                            <div className="flex items-center gap-1">
                                {analysis.trendDirection === 'improving' ? (
                                    <>
                                        <TrendingDown
                                            size={16}
                                            className="text-green-600"
                                        />
                                        <span className="text-sm font-semibold text-green-600">
                                            {t('reports.analysis.improving')}
                                        </span>
                                    </>
                                ) : analysis.trendDirection === 'worsening' ? (
                                    <>
                                        <TrendingUp
                                            size={16}
                                            className="text-red-600"
                                        />
                                        <span className="text-sm font-semibold text-red-600">
                                            {t('reports.analysis.worsening')}
                                        </span>
                                    </>
                                ) : (
                                    <>
                                        <Minus
                                            size={16}
                                            className="text-gray-500"
                                        />
                                        <span className="text-sm font-semibold text-gray-600">
                                            {t('reports.analysis.stable')}
                                        </span>
                                    </>
                                )}
                            </div>
                            <p className="text-xs text-gray-500 mt-1">
                                σ = {analysis.volatility.toFixed(2)}%
                            </p>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}

export default CancellationRateChart
